// javascript
import { Swiper, SwiperSlide } from 'swiper/react';
import { Navigation, Mousewheel } from 'swiper/modules';
import ProjectLightbox from './ProjectLightbox.jsx';
import React, { useRef, useMemo, useState, useEffect } from 'react';
import {l} from "../../../lang/index.jsx";


export default function ProjectImagesSwiper({ currentImages, titleProject, lang, uiMap }) {
    const galleryTitle = uiMap?.['single-gallery-title']?.[lang] || l('single-gallery-title', lang);
    const swiperRef = useRef(null);
    const [lbOpen, setLbOpen] = useState(false);
    const [lbIndex, setLbIndex] = useState(0);
    // Etat pour savoir si on est au début / à la fin du slider (utile quand loop = false)
    const [isBeginning, setIsBeginning] = useState(true);
    const [isEnd, setIsEnd] = useState(false);

    // Si des images du projet courant sont fournies, prépare-les proprement
    const projectImages = useMemo(() => {
        if (!currentImages || !Array.isArray(currentImages) || currentImages.length === 0) return null;
        return currentImages.map((it) => {
            // chaque élément attendu : { img: '/path' } ou string
            if (typeof it === 'string') return { img: it };
            if (it && (it.img || it.src)) return { img: it.img || it.src };
            return null;
        }).filter(Boolean);
    }, [currentImages]);

    // verrouille le scroll du body quand la lightbox est ouverte
    useEffect(() => {
        if (typeof document === 'undefined') return;
        const prev = document.body.style.overflow;
        if (lbOpen) document.body.style.overflow = 'hidden';
        else document.body.style.overflow = prev;
        return () => { document.body.style.overflow = prev; };
    }, [lbOpen]);

    // Si pas d'images de projet, ne rien afficher (user voulait pas de fallback)
    if (!projectImages || projectImages.length === 0) return null;

    const openLightbox = (index) => {
        setLbIndex(index);
        setLbOpen(true);
    };

    const closeLightbox = () => setLbOpen(false);

    const prevLightbox = () => setLbIndex((i) => (i - 1 + projectImages.length) % projectImages.length);
    const nextLightbox = () => setLbIndex((i) => (i + 1) % projectImages.length);

    // Handlers pour la navigation custom (prévenir le comportement par défaut des <a>)
    const prevSlide = (e) => {
        if (e && typeof e.preventDefault === 'function') e.preventDefault();
        // si on est déjà au début et qu'il n'y a pas de loop, ne fait rien
        if (swiperRef.current && swiperRef.current.isBeginning) return;
        if (swiperRef.current && typeof swiperRef.current.slidePrev === 'function') {
            swiperRef.current.slidePrev();
        }
    };

    const nextSlide = (e) => {
        if (e && typeof e.preventDefault === 'function') e.preventDefault();
        // si on est déjà à la fin et qu'il n'y a pas de loop, ne fait rien
        if (swiperRef.current && swiperRef.current.isEnd) return;
        if (swiperRef.current && typeof swiperRef.current.slideNext === 'function') {
            swiperRef.current.slideNext();
        }
    };

    return (

            <div className="swiper-container swiper-gallery-inner">
                <h2 className="h2">{galleryTitle}</h2>
                <Swiper
                    modules={[Navigation, Mousewheel]}
                    className="swiper-gallery"
                    slidesPerView='1'
                    spaceBetween='20px'
                    speed={600}
                    // onSwiper nous donne l'instance initiale du swiper
                    onSwiper={(s) => {
                        swiperRef.current = s;
                        // initialiser l'état début/fin
                        if (s) {
                            setIsBeginning(!!s.isBeginning);
                            setIsEnd(!!s.isEnd);
                        }
                    }}
                    // update des flags quand la slide change
                    onSlideChange={(s) => {
                        setIsBeginning(!!s.isBeginning);
                        setIsEnd(!!s.isEnd);
                    }}
                     breakpoints={{
                         768: {
                             slidesPerView: 1.5
                         }
                     }}
                >
                    {
                        projectImages.map((p, i) => (
                            <SwiperSlide key={p.img ?? i}>
                                <article className='nav-card project-slide'>
                                    <div className='card-group'>
                                        <div className='card-background cursor-event'>
                                            <img src={p.img} alt={`Image project ${titleProject} ${i}`} loading="lazy" onClick={() => openLightbox(i)} />
                                        </div>
                                    </div>
                                </article>
                            </SwiperSlide>
                        ))
                    }
                </Swiper>
                <div className='nav-swiper'>
                    <a
                        href="#"
                        className={`picto prev ${isBeginning ? 'disabled' : ''}`}
                        aria-disabled={isBeginning}
                        onClick={prevSlide}
                        aria-label="Previous image"
                    >
                        <div className="arrow-container">
                            <svg className="arrow-down" xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="currentColor">
                                <polygon points="11.293 4.707 17.586 11 4 11 4 13 17.586 13 11.293 19.293 12.707 20.707 21.414 12 12.707 3.293 11.293 4.707"/>
                            </svg>
                        </div>
                    </a>
                    <a
                        href="#"
                        className={`picto next ${isEnd ? 'disabled' : ''}`}
                        aria-disabled={isEnd}
                        onClick={nextSlide}
                        aria-label="Next image"
                    >
                        <div className="arrow-container">
                            <svg className="arrow-down" xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="currentColor">
                                <polygon points="11.293 4.707 17.586 11 4 11 4 13 17.586 13 11.293 19.293 12.707 20.707 21.414 12 12.707 3.293 11.293 4.707"/>
                            </svg>
                        </div>
                    </a>
                </div>

                <ProjectLightbox images={projectImages} index={lbIndex} open={lbOpen} onClose={closeLightbox} onPrev={prevLightbox} onNext={nextLightbox} />
            </div>

    )
}
